import { capitalizeFirstLetter } from '@shared/lib/utils';

// Generic filter item type that matches the API structure
type FilterItem = {
  name?: string;
  uuid?: string;
};

// Generic filters type that can contain arrays of filter items or other types
type FiltersSource = Record<string, any>;

// Type for individual filter option
export type FilterOption = {
  label: string;
  value: string;
};

// Type for filter options object with specific keys
export type FilterOptions<T extends readonly string[]> = {
  [K in T[number]]: FilterOption[];
};

// Helper type to create filter result object with "Filter" suffix
type FilterResult<T extends readonly string[]> = {
  [K in T[number] as `${K}Filter`]: Array<{ label: string; value: string }>;
};

export const useServerFilterOptions = <
  TFilters extends FiltersSource,
  TFilterKeys extends readonly string[],
>(
  filterKeys: TFilterKeys,
  filtersSource: TFilters
): FilterResult<TFilterKeys> => {
  const result = {} as FilterResult<TFilterKeys>;

  filterKeys.forEach((key) => {
    const filterData = filtersSource[key];
    const filterKey = `${String(key)}Filter` as keyof FilterResult<TFilterKeys>;

    // Check if it's an array of filter items with name/uuid structure
    if (Array.isArray(filterData)) {
      result[filterKey] = filterData.map((filter: FilterItem) => ({
        label: filter.name ? capitalizeFirstLetter(filter.name) : '',
        value: filter.uuid || filter.name || '',
      })) as FilterResult<TFilterKeys>[keyof FilterResult<TFilterKeys>];
    } else {
      // Return empty array for non-array types (like sources enum)
      result[filterKey] =
        [] as FilterResult<TFilterKeys>[keyof FilterResult<TFilterKeys>];
    }
  });

  return result;
};

// Keep the old hook for backward compatibility
export const useCreateServerFilterOptions = <
  TFilters extends FiltersSource,
>() => {
  const createFilterOptions = (
    filterKey: keyof TFilters,
    filtersSource: TFilters
  ): Array<{ label: string; value: string }> => {
    const filterData = filtersSource[filterKey];

    // Check if it's an array of filter items with name/uuid structure
    if (Array.isArray(filterData)) {
      return filterData.map((filter: FilterItem) => ({
        label: filter.name || '',
        value: filter.uuid || '',
      }));
    }

    // Return empty array for non-array types (like sources enum)
    return [];
  };

  return createFilterOptions;
};
